import React from 'react';
import { Field, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { icon_arrow_left, icon_arrow_right, icon_error } from '@gn-audio/icons';
import {
  Button,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
} from '@gn-audio/jss-components';
import { tailwindConfig } from '@gn-audio/jss-components/tailwind-presets';

import { Slider } from 'components/Product/Slider/Slider';
import { SliderProps } from 'components/Product/Slider/sliderTypes';

export type AlternativeSuggestionsSpotProps = {
  fields: AlternativeSuggestionsSpotFields;
};

export type AlternativeSuggestionsSpotFields = {
  MainButtonText: Field<string>;
  MainButtonAriaLabel?: Field<string>;
  MainButtonLink: LinkField;
  SecondaryButtonText: Field<string>;
  SecondaryButtonAriaLabel?: Field<string>;
  SecondaryButtonLink: LinkField;
  Title: Field<string>;
  Description: Field<string>;
  background: Field<string>;
  Sliders: SliderProps[];
};

const AlternativeSuggestionsSpot = ({
  fields,
}: AlternativeSuggestionsSpotProps): React.ReactElement => {
  const {
    Title,
    Description,
    MainButtonText,
    MainButtonAriaLabel,
    SecondaryButtonText,
    SecondaryButtonAriaLabel,
    MainButtonLink,
    SecondaryButtonLink,
    background,
    Sliders,
  } = fields;

  const spotThemeClass = getContrastThemeClass(background?.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(background?.value as string) || {
    backgroundColor: tailwindConfig.theme.colors.beige[50],
  };

  return (
    <div
      className={classNames('container-fluid overflow-hidden', spotThemeClass)}
      style={spotBackgroundStyle}
    >
      <div className="container !py-0">
        <div className="flex flex-col items-center justify-center gap-8 pb-28 align-top">
          <div className="flex size-20 items-center justify-center rounded-full bg-black/10">
            <JabraIcon className="size-10" Icon={icon_error} />
          </div>
          <div>
            <h1
              className="heading-1 text-center"
              dangerouslySetInnerHTML={{ __html: sanitize(Title?.value as string) }}
            />
            <p
              className="paragraph-large mb-6 text-center"
              dangerouslySetInnerHTML={{ __html: sanitize(Description?.value as string) }}
            />
          </div>
          <div className="buttonRow flex flex-col gap-4 md:flex-row md:gap-2">
            {!!MainButtonText?.value && (
              <Button
                asLink
                href={MainButtonLink?.value.href}
                variant="primary"
                size="m"
                {...(MainButtonAriaLabel?.value && { 'aria-label': MainButtonAriaLabel.value })}
                iconPlacement="left"
                icon={<JabraIcon Icon={icon_arrow_left} />}
              >
                {MainButtonText?.value}
              </Button>
            )}
            {!!SecondaryButtonText?.value && (
              <Button
                asLink
                href={SecondaryButtonLink?.value.href}
                variant="secondary"
                size="m"
                {...(SecondaryButtonAriaLabel?.value && {
                  'aria-label': SecondaryButtonAriaLabel.value,
                })}
                iconPlacement="right"
                icon={<JabraIcon Icon={icon_arrow_right} />}
              >
                {SecondaryButtonText?.value}
              </Button>
            )}
          </div>
        </div>
        {Sliders?.map((slider) => (
          <Slider key={slider.id} header={slider.header} cards={slider.cards} id={slider.id} />
        ))}
      </div>
    </div>
  );
};

export default AlternativeSuggestionsSpot;
